import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * InputGroup — a shadcn-style wrapper that combines a leading/trailing
 * text addon with an input inside a single bordered container.
 *
 * Usage:
 * ```tsx
 * <InputGroup>
 *   <InputGroupAddon>https://</InputGroupAddon>
 *   <InputGroupInput placeholder="example.com" />
 * </InputGroup>
 * ```
 */

const InputGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { hasValue?: boolean }
>(({ className, hasValue, ...props }, ref) => (
  <div
    ref={ref}
    data-has-value={hasValue || undefined}
    className={cn(
      "group/ig flex h-9 w-full items-center rounded-md border border-input bg-background text-sm transition-colors focus-within:ring-1 focus-within:ring-ring",
      className,
    )}
    {...props}
  />
));
InputGroup.displayName = "InputGroup";

const InputGroupAddon = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      "flex shrink-0 items-center px-3 text-sm text-muted-foreground select-none border-r border-input bg-muted/40 transition-colors group-data-[has-value]/ig:text-foreground",
      className,
    )}
    {...props}
  />
));
InputGroupAddon.displayName = "InputGroupAddon";

const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      "flex h-full w-full min-w-0 bg-transparent px-3 py-1 text-sm outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-muted-foreground",
      className,
    )}
    {...props}
  />
));
InputGroupInput.displayName = "InputGroupInput";

export { InputGroup, InputGroupAddon, InputGroupInput };
